import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function QuickView({ product, onClose }) {
  const { addItem } = useCart();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (product) {
      const first = product.variants?.[0];
      if (first?.options) setSelectedOptions(first.options);
      setQty(1);
      setImgIdx(0);
    }
  }, [product]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const matchingVariant = useMemo(() => {
    if (!product?.variants) return null;
    return product.variants.find((v) =>
      Object.entries(selectedOptions).every(([k, val]) => v.options?.[k] === val)
    ) || product.variants[0];
  }, [product, selectedOptions]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const seen = new Set();
    const list = [];
    const add = (url) => { if (url && !seen.has(url)) { seen.add(url); list.push(url); } };
    add(product.image);
    (product.images || []).forEach(add);
    (product.variants || []).forEach((v) => add(v.image));
    return list;
  }, [product]);

  if (!product) return null;

  const optionGroups = product.variants?.length ? Object.keys(product.variants[0].options || {}) : [];
  const availableValuesFor = (optName) =>
    Array.from(new Set(product.variants.map((v) => v.options?.[optName]).filter(Boolean)));

  const handleAdd = () => {
    if (!matchingVariant) return toast.error("Select options");
    if (!matchingVariant.available) return toast.error("Sold out");
    addItem(product, matchingVariant, qty);
    toast.success(`${product.name} added to bag`);
    onClose();
  };

  const price = matchingVariant?.price ?? product.price;
  const compareAt = matchingVariant?.compareAtPrice;
  const hasDiscount = compareAt && parseFloat(compareAt) > price;
  const brand = (product.brand === "SELFNAMED" || product.brand === "Selfnamed") ? "DORIA" : product.brand;

  const prevImg = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const nextImg = () => setImgIdx((i) => (i + 1) % gallery.length);

  // Filter out default title options
  const visibleOptions = optionGroups.filter((optName) => {
    const values = availableValuesFor(optName);
    if (values.length <= 1 && (values[0] || "").toLowerCase().includes("default")) return false;
    if (optName.toLowerCase() === "title" && values.length <= 1) return false;
    return true;
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70]"
        data-testid="quickview-backdrop"
      />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        className="fixed inset-0 z-[71] flex items-center justify-center p-3 sm:p-6 pointer-events-none"
        data-testid="quickview-modal"
      >
        <div className="pointer-events-auto bg-white w-full max-w-[1100px] max-h-[92vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden">
          {/* Close */}
          <button
            onClick={onClose}
            data-testid="quickview-close"
            className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-sm"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#333333]" />
          </button>

          {/* Main content: scrollable left + sticky right */}
          <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
            {/* LEFT — Scrollable product details */}
            <div className="md:w-[45%] overflow-y-auto p-6 sm:p-8 lg:p-10 border-r border-[#333333]/05">
              <p className="text-[11px] tracking-[0.25em] uppercase font-sans font-medium text-[#333333]/40">{brand}</p>
              <h2 className="font-serif text-2xl sm:text-3xl leading-tight tracking-tight mt-2 text-[#333333]" data-testid="quickview-title">
                {product.name}
              </h2>

              {product.description && (
                <p className="font-sans text-sm text-[#333333]/60 leading-relaxed mt-5">
                  {product.description}
                </p>
              )}

              {/* Key Ingredients */}
              <div className="mt-8">
                <p className="font-serif text-base font-medium text-[#333333] mb-3">Active Ingredients</p>
                <ul className="space-y-1.5 list-disc list-inside font-sans text-sm text-[#333333]/60">
                  <li>Hyaluronic Acid</li>
                  <li>Niacinamide 5%</li>
                  <li>Squalane</li>
                  <li>Vitamin E</li>
                </ul>
              </div>

              {/* How to Use */}
              <div className="mt-8">
                <p className="font-serif text-base font-medium text-[#333333] mb-3">How to Use</p>
                <p className="font-sans text-sm text-[#333333]/60 leading-relaxed">
                  Apply to clean, damp skin. Use morning and evening for best results. Follow with moisturizer and SPF.
                </p>
              </div>

              {/* Variants */}
              {visibleOptions.map((optName) => (
                <div key={optName} className="mt-8">
                  <p className="font-sans text-sm text-[#333333]/70 mb-3">{optName}:</p>
                  <div className="flex flex-wrap gap-2">
                    {availableValuesFor(optName).map((val) => (
                      <button
                        key={val}
                        onClick={() => setSelectedOptions((p) => ({ ...p, [optName]: val }))}
                        data-testid={`quickview-option-${val}`}
                        className={`text-sm font-sans px-4 py-2 rounded-full border transition-all ${
                          selectedOptions[optName] === val
                            ? "bg-[#333333] text-white border-[#333333]"
                            : "border-[#333333]/15 text-[#333333] hover:border-[#333333]/40"
                        }`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Quantity */}
              <div className="mt-8">
                <div className="inline-flex items-center border border-[#333333]/15 rounded-lg">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 flex items-center justify-center text-[#333333]/40 hover:text-[#333333] transition-colors" data-testid="quickview-qty-dec">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-sans text-sm text-[#333333]">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-12 h-12 flex items-center justify-center text-[#333333]/40 hover:text-[#333333] transition-colors" data-testid="quickview-qty-inc">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT — Sticky image gallery */}
            <div className="md:w-[55%] bg-[#F7F7F5] flex flex-col items-center justify-center p-6 sm:p-8 relative">
              {/* Main image */}
              <div className="relative w-full aspect-square flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={imgIdx}
                    src={gallery[imgIdx] || product.image}
                    alt={product.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-full max-h-full object-contain"
                  />
                </AnimatePresence>

                {/* Prev/Next arrows */}
                {gallery.length > 1 && (
                  <>
                    <button onClick={prevImg} className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm hover:bg-white transition-colors shadow-sm" aria-label="Previous image">
                      <ChevronLeft className="w-5 h-5 text-[#333333]" />
                    </button>
                    <button onClick={nextImg} className="absolute right-16 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm hover:bg-white transition-colors shadow-sm" aria-label="Next image">
                      <ChevronRight className="w-5 h-5 text-[#333333]" />
                    </button>
                  </>
                )}
              </div>

              {/* Vertical thumbnail strip — right edge */}
              {gallery.length > 1 && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                  {gallery.slice(0, 5).map((url, i) => (
                    <button
                      key={url}
                      onClick={() => setImgIdx(i)}
                      data-testid={`quickview-thumb-${i}`}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${imgIdx === i ? "border-[#333333]" : "border-transparent hover:border-[#333333]/20"}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sticky bottom bar */}
          <div className="border-t border-[#333333]/08 px-6 sm:px-8 py-4 flex items-center gap-3 bg-white flex-shrink-0">
            <div className="mr-auto">
              {hasDiscount ? (
                <div className="flex items-center gap-2">
                  <span className="font-serif text-xl text-red-600 font-medium">${price.toFixed(2)}</span>
                  <span className="font-sans text-sm text-[#333333]/30 line-through">${parseFloat(compareAt).toFixed(2)}</span>
                </div>
              ) : (
                <span className="font-serif text-xl text-[#333333]">${price.toFixed(2)}</span>
              )}
            </div>
            <button
              onClick={handleAdd}
              disabled={!matchingVariant?.available}
              data-testid="quickview-add"
              className="px-7 py-3 bg-[#333333] text-white rounded-full text-[11px] tracking-[0.2em] uppercase font-sans font-medium hover:bg-[#1a1a1a] disabled:opacity-40 transition-colors"
            >
              {matchingVariant?.available === false ? "Sold out" : "Add to cart"}
            </button>
            <Link
              to={`/product/${product.id}`}
              onClick={onClose}
              data-testid="quickview-full-details"
              className="px-6 py-3 border border-[#333333]/15 rounded-full text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] hover:bg-[#333333] hover:text-white transition-colors"
            >
              View details
            </Link>
            <button
              onClick={handleAdd}
              disabled={!matchingVariant?.available}
              className="px-6 py-3 border border-[#333333]/15 rounded-full text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] hover:bg-[#333333] hover:text-white transition-colors hidden sm:block"
            >
              Buy it now
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
